/**
 * Currency Toggle Composable
 * Manages SOL/USD price conversion for analytics charts and displays
 */

// Currency mode constants
export const CURRENCY_MODES = {
    SOL: 'sol',
    USD: 'usd'
};

// Currency labels
export const CURRENCY_LABELS = {
    [CURRENCY_MODES.SOL]: 'SOL',
    [CURRENCY_MODES.USD]: 'USD'
};

// Price cache - module-level singleton
let priceCache = {
    current: null,
    prices: {},
    lastFetch: null,
    fetching: false
};

/**
 * Fetch SOL prices for a date range.
 * Results are cached at module level to avoid duplicate requests.
 *
 * @param {Date|string} from Start date
 * @param {Date|string} to End date
 * @returns {Promise<{current: number|null, prices: Object}>}
 */
export async function fetchPrices(from, to) {
    // Avoid duplicate concurrent fetches
    if (priceCache.fetching) {
        await new Promise(resolve => setTimeout(resolve, 100));
        return { current: priceCache.current, prices: priceCache.prices };
    }

    // Return cached data if recent (within 5 minutes)
    if (priceCache.lastFetch && Date.now() - priceCache.lastFetch < 300000) {
        return { current: priceCache.current, prices: priceCache.prices };
    }

    priceCache.fetching = true;

    try {
        const params = new URLSearchParams();
        if (from) params.set('from', new Date(from).toISOString());
        if (to) params.set('to', new Date(to).toISOString());

        const response = await fetch(`/api/prices/sol-usd?${params}`);
        if (response.ok) {
            const data = await response.json();
            priceCache.current = data.current;
            priceCache.prices = data.prices || {};
            priceCache.lastFetch = Date.now();
        }
    } catch (error) {
        console.error('Failed to fetch SOL prices:', error);
    } finally {
        priceCache.fetching = false;
    }

    return { current: priceCache.current, prices: priceCache.prices };
}

/**
 * Get the current cached SOL price in USD.
 * @returns {number|null}
 */
export function getCurrentPrice() {
    return priceCache.current;
}

/**
 * Get the price for a specific date.
 * @param {Date|string} date
 * @returns {number|null}
 */
export function getPriceForDate(date) {
    const dateStr = new Date(date).toISOString().split('T')[0];
    return priceCache.prices[dateStr] || priceCache.current;
}

/**
 * Convert SOL to USD.
 * @param {number} solAmount Amount in SOL
 * @param {number|null} price Optional specific price to use
 * @returns {number|null}
 */
export function solToUsd(solAmount, price = null) {
    const usePrice = price ?? priceCache.current;
    if (usePrice === null || usePrice === undefined) return null;
    return solAmount * usePrice;
}

/**
 * Format a SOL value based on currency mode.
 * @param {number} solValue Value in SOL
 * @param {string} mode Currency mode (sol or usd)
 * @param {number|null} price Optional specific price for USD conversion
 * @returns {string}
 */
export function formatCurrencyValue(solValue, mode, price = null) {
    if (solValue === null || solValue === undefined) return '-';

    if (mode === CURRENCY_MODES.USD) {
        const usdValue = solToUsd(solValue, price);
        if (usdValue === null) return '-';
        return formatUsd(usdValue);
    }

    return formatSol(solValue);
}

/**
 * Format a USD value.
 * @param {number} value
 * @returns {string}
 */
export function formatUsd(value) {
    if (value === null || value === undefined) return '-';
    if (value >= 1e9) return '$' + (value / 1e9).toFixed(2) + 'B';
    if (value >= 1e6) return '$' + (value / 1e6).toFixed(2) + 'M';
    if (value >= 1e3) return '$' + (value / 1e3).toFixed(2) + 'K';
    if (value >= 1) return '$' + value.toFixed(2);
    if (value >= 0.01) return '$' + value.toFixed(4);
    return '$' + value.toFixed(6);
}

/**
 * Format a SOL value.
 * @param {number} value
 * @returns {string}
 */
export function formatSol(value) {
    if (value === null || value === undefined) return '-';
    if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M SOL';
    if (value >= 1e3) return (value / 1e3).toFixed(2) + 'K SOL';
    if (value >= 1) return value.toFixed(2) + ' SOL';
    return value.toFixed(6) + ' SOL';
}

/**
 * Get the currency symbol for display.
 * @param {string} mode
 * @returns {string}
 */
export function getCurrencySymbol(mode) {
    return mode === CURRENCY_MODES.USD ? '$' : 'SOL';
}

/**
 * Get the currency unit for labels.
 * @param {string} mode
 * @returns {string}
 */
export function getCurrencyUnit(mode) {
    return mode === CURRENCY_MODES.USD ? 'USD' : 'SOL';
}

/**
 * Transform an array of data to include USD values.
 * @param {Array} data Array of objects with SOL values
 * @param {Array<string>} fields Field names that contain SOL values
 * @returns {Array} Transformed data with additional _usd fields
 */
export function transformDataWithUsd(data, fields = ['fees', 'revenue', 'tips']) {
    if (!Array.isArray(data)) return data;

    return data.map(item => {
        const transformed = { ...item };
        const datePrice = item.timestamp ? getPriceForDate(item.timestamp) : priceCache.current;

        fields.forEach(field => {
            if (typeof item[field] === 'number') {
                transformed[`${field}_usd`] = solToUsd(item[field], datePrice);
            }
        });

        return transformed;
    });
}

/**
 * Vue composable wrapper for currency toggle functionality.
 */
export function useCurrencyToggle() {
    return {
        CURRENCY_MODES,
        CURRENCY_LABELS,
        fetchPrices,
        getCurrentPrice,
        getPriceForDate,
        solToUsd,
        formatCurrencyValue,
        formatUsd,
        formatSol,
        getCurrencySymbol,
        getCurrencyUnit,
        transformDataWithUsd
    };
}

export default useCurrencyToggle;
